// TODO @consumer: Rewrite PhotoObfuscated and replace it in this component.
// TODO @consumer: Rewrite "DownloadPhotoButton" and probably convert to hook.

import React, { useMemo, Fragment, useEffect, useState, useRef } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { useHistory } from 'react-router-dom';

import { IMAGE_SIZE_CODE } from '../../../../../../../constants/image-size-codes';
import PhotoObfuscated from '../../../../../../../components/global/obfuscation/PhotoObfuscated';
import Badge from '../../../../../../../components/global/Badge';
import { getFaceboxes, getMatchedPhotoJerseyColors } from './helpers';
import { IMAGE_BLUR_TYPES } from '../../../../../../../constants/image-blur-types';
import MatchedPhotoBackgroundSelectionActions from './MatchedPhotoBackgroundSelectionActions';
import useMatchedPhotoPrintBackgrounds from './hooks/useMatchedPhotoPrintBackgrounds';
import {
  FACEBLOCK_MODE,
  MATCHED_PHOTO_WATERMARK_MODE,
  WALLPAPER_WATERMARK_MODE,
} from '../../../../../../../constants/pro-settings';
import useYearbookPhotoMutations from '../../../../../../../components/global/hooks/useYearbookPhotoMutations';
import usePhotoCrop from '../../../../../../../components/global/hooks/usePhotoCrop';
import useRequestConfirm from '../../../../../../../hooks/useRequestConfirm';
import CustomControl from '../../../../../../../components/global/CustomControl';
import useAlbumFolderYearbookSettings from '../../../../../../../components/global/hooks/useAlbumFolderYearbookSettings';
import { getThumbBySize } from '../../../../../../../util/photo';
import { isYearbookEntity } from '../../../../../../../util/product-permissions';
import useIdentityJerseyColors from '../../hooks/useIdentityJerseyColors';
import WatermarkOverlay from '../../../../../../../components/global/WatermarkOverlay';

const propTypes = {
  matchedPhoto: PropTypes.shape({
    id: PropTypes.string.isRequired,
    photo: PropTypes.shape({
      id: PropTypes.string.isRequired,
      thumbnail_urls: PropTypes.arrayOf(
        PropTypes.shape({
          url: PropTypes.string,
          width: PropTypes.number,
          height: PropTypes.number,
          size_code: PropTypes.oneOf(Object.values(IMAGE_SIZE_CODE)),
          regions: PropTypes.arrayOf(PropTypes.arrayOf(PropTypes.number)),
        }),
      ),
      price_album_folder_tag: PropTypes.shape({
        id: PropTypes.string.isRequired,
        name: PropTypes.string.isRequired,
        digital_downloads_price: PropTypes.shape({
          value: PropTypes.number.isRequired,
        }).isRequired,
      }),
      wallpaper_watermark_urls: PropTypes.array,
      attribution: PropTypes.shape({
        name: PropTypes.string,
        username: PropTypes.string,
        avatar_photo: PropTypes.shape({
          thumbnail_urls: PropTypes.array,
        }),
      }),
    }).isRequired,
  }).isRequired,
  isEntitled: PropTypes.bool.isRequired,
  isPictureDay: PropTypes.bool.isRequired,
  isYearbookEvent: PropTypes.bool.isRequired,
  albumFolderId: PropTypes.string,
  identityId: PropTypes.string,
  existingYearbookMatchedPhotoId: PropTypes.string,
  actions: PropTypes.node,
  isAllPhotos: PropTypes.bool,
  includeTagName: PropTypes.bool,
  linkTo: PropTypes.string,
  withWatermark: PropTypes.bool,
  printBackgrounds: PropTypes.array,
  faceblockMode: PropTypes.oneOf(Object.values(FACEBLOCK_MODE)),
  className: PropTypes.string,
  displayWallpaper: PropTypes.bool,
  watermarkSettings: PropTypes.shape({
    watermarkMode: PropTypes.string,
    wallpaperMode: PropTypes.string,
    wallpaperWatermarkMode: PropTypes.string,
    wallpaperCustomText: PropTypes.string,
    wallpaperFontSize: PropTypes.number,
    faceblockMode: PropTypes.string,
    faceblockCustomIconAssetImage: PropTypes.shape({
      url: PropTypes.string,
    }),
    wallpaperAssetImage: PropTypes.shape({
      url: PropTypes.string,
    }),
  }),
  account: PropTypes.shape({
    id: PropTypes.string,
  }),
  vendorAccount: PropTypes.shape({
    id: PropTypes.string,
  }),
};

const MatchedPhoto = ({
  matchedPhoto,
  isEntitled,
  isPictureDay,
  isYearbookEvent,
  albumFolderId,
  identityId,
  existingYearbookMatchedPhotoId,
  actions,
  isAllPhotos,
  includeTagName = true,
  linkTo,
  withWatermark,
  printBackgrounds,
  faceblockMode,
  className,
  watermarkSettings,
  displayWallpaper,
  account,
  vendorAccount,
}) => {
  const requestConfirm = useRequestConfirm();
  const { jerseyColors: identityJerseyColors } = useIdentityJerseyColors({
    identityId,
    albumFolderId,
    isGreenScreenEnabled: printBackgrounds?.length > 0,
  });
  const matchedPhotoJerseyColors = getMatchedPhotoJerseyColors(matchedPhoto);
  const availableColors = useMemo(() => {
    if (!matchedPhotoJerseyColors?.length || !identityJerseyColors?.length) {
      return [];
    }

    return matchedPhotoJerseyColors.filter(color =>
      identityJerseyColors.includes(color),
    );
  }, [identityJerseyColors, matchedPhotoJerseyColors]);

  const availablePrintBackgrounds = useMemo(() => {
    if (!availableColors.length) {
      return printBackgrounds;
    }
    return printBackgrounds.filter(
      ({ jersey_colors: backgroundColors }) =>
        !!backgroundColors.records.find(({ jersey_color: color }) =>
          availableColors.includes(color),
        ),
    );
  }, [availableColors, printBackgrounds]);

  const {
    selectedPrintBackground,
    onPrevBackground,
    onNextBackground,
    swipeHandlers,
  } = useMatchedPhotoPrintBackgrounds({
    matchedPhoto,
    printBackgrounds: availablePrintBackgrounds,
  });

  const {
    onYearbookPhotosSet,
    onYearbookPhotoDelete,
  } = useYearbookPhotoMutations();

  const { albumFolder } = useAlbumFolderYearbookSettings({
    albumFolderId: isYearbookEvent ? albumFolderId : undefined,
  });
  const { onFaceCenterCrop } = usePhotoCrop({});
  const history = useHistory();

  const {
    id,
    photo,
    identity,
    album,
    yearbook_photo: yearbookPhoto,
    faceblocked_thumbnail_urls: matchedFaceblockedThumbnails,
  } = matchedPhoto;

  const tagName = matchedPhoto.price_album_folder_tag?.name;

  const foregroundThumbnails = useMemo(() => {
    if (!photo?.foreground?.original_url) {
      return [];
    }

    if (
      !isEntitled &&
      photo?.foreground?.watermarked_thumbnail_urls?.length > 0
    ) {
      return photo?.foreground?.watermarked_thumbnail_urls;
    }

    if (photo?.foreground?.thumbnail_urls?.length > 0) {
      return photo?.foreground?.thumbnail_urls;
    }

    return [photo?.foreground?.original_url];
  }, [photo, isEntitled]);

  const faceblockedThumbnails = useMemo(() => matchedFaceblockedThumbnails, [
    matchedPhoto,
  ]);

  const hasForegroundFaceblockedUrls = useMemo(() => {
    return !!(matchedFaceblockedThumbnails ?? [])?.find(
      ({ blur_type: blurType }) =>
        blurType === IMAGE_BLUR_TYPES.PHOTO_FOREGROUND_FOREGROUND,
    );
  }, [matchedPhoto]);

  const watermarkedThumbnails = useMemo(() => {
    return photo?.wallpaper_watermark_urls;
  }, [photo]);

  const shouldUseForegroundUrls = useMemo(
    () => foregroundThumbnails?.length > 0,
    [photo],
  );

  const enableWatermark =
    isPictureDay &&
    (displayWallpaper ||
      (watermarkSettings?.watermarkMode ===
        MATCHED_PHOTO_WATERMARK_MODE.WALLPAPER &&
        (watermarkSettings?.wallpaperWatermarkMode ===
          WALLPAPER_WATERMARK_MODE.TEXT ||
          watermarkSettings?.wallpaperWatermarkMode ===
            WALLPAPER_WATERMARK_MODE.IMAGE))) &&
    (!photo?.foreground?.original_url || hasForegroundFaceblockedUrls) &&
    !isEntitled;

  const enableFaceblock =
    isPictureDay &&
    watermarkSettings?.watermarkMode ===
      MATCHED_PHOTO_WATERMARK_MODE.FACEBLOCK &&
    !displayWallpaper &&
    (!photo?.foreground?.original_url || hasForegroundFaceblockedUrls) &&
    !isEntitled;

  const shouldUseWatermarkedUrls = useMemo(
    () =>
      photo?.wallpaper_watermark_urls?.length > 0 &&
      enableWatermark &&
      !isEntitled,
    [photo, enableWatermark, isEntitled],
  );

  const shouldUseFaceblockedUrls = useMemo(
    () =>
      (!shouldUseForegroundUrls || hasForegroundFaceblockedUrls) &&
      !shouldUseWatermarkedUrls &&
      faceblockedThumbnails?.length > 0 &&
      !isEntitled &&
      isPictureDay,
    [
      photo,
      shouldUseForegroundUrls,
      hasForegroundFaceblockedUrls,
      isEntitled,
      isPictureDay,
    ],
  );

  const faceboxes = useMemo(() => {
    if (shouldUseFaceblockedUrls && hasForegroundFaceblockedUrls) {
      const foregroundThumb = faceblockedThumbnails.find(
        ({ blur_type: blurType }) =>
          blurType === IMAGE_BLUR_TYPES.PHOTO_FOREGROUND_FOREGROUND,
      );

      if (foregroundThumb?.regions) {
        return foregroundThumb?.regions;
      }
    }

    if (shouldUseForegroundUrls) {
      return [];
    }

    if (shouldUseFaceblockedUrls) {
      const foregroundThumb = faceblockedThumbnails.find(
        ({ blur_type: blurType }) => blurType === IMAGE_BLUR_TYPES.FOREGROUND,
      );

      if (foregroundThumb?.regions) {
        return foregroundThumb?.regions;
      }
    }

    return getFaceboxes(matchedPhoto, !isAllPhotos);
  }, [matchedPhoto, isAllPhotos, isEntitled, isPictureDay]);

  const thumbs = useMemo(() => {
    if (shouldUseFaceblockedUrls) {
      return faceblockedThumbnails;
    }

    if (foregroundThumbnails.length > 0) {
      return foregroundThumbnails;
    }

    if (shouldUseWatermarkedUrls) {
      return watermarkedThumbnails;
    }

    return withWatermark
      ? photo.watermarked_urls ?? photo.thumbnail_urls
      : photo.thumbnail_urls;
  }, [photo, withWatermark, isEntitled, isPictureDay]);

  const obfuscatorMultiplier = useMemo(() => {
    if (shouldUseFaceblockedUrls && shouldUseForegroundUrls) {
      return 1;
    }

    if (shouldUseForegroundUrls) {
      return 0;
    }

    if (shouldUseFaceblockedUrls) {
      return 1;
    }

    return isEntitled || isAllPhotos ? 0 : 1.8;
  }, [isEntitled, isPictureDay, isAllPhotos]);

  const maxThumbSize = useMemo(() => {
    if (shouldUseFaceblockedUrls && shouldUseForegroundUrls) {
      return IMAGE_SIZE_CODE.MEDIUM2X;
    }

    if (shouldUseFaceblockedUrls) {
      return IMAGE_SIZE_CODE.LARGE;
    }

    // On non PD FWG, non-entitled items must show on medium res.
    return !isEntitled && isAllPhotos && !isPictureDay
      ? IMAGE_SIZE_CODE.MEDIUM2X
      : IMAGE_SIZE_CODE.LARGE;
  }, [isEntitled, isPictureDay, isAllPhotos]);

  const printBackgroundStyles = useMemo(() => {
    if (
      foregroundThumbnails.length === 0 ||
      !selectedPrintBackground?.asset_image
    ) {
      return {};
    }

    return {
      backgroundSize: 'cover',
      backgroundPosition: 'center',
      backgroundImage: `url(${selectedPrintBackground.asset_image.url})`,
    };
  }, [selectedPrintBackground]);

  const faceblockerImageUrl = useMemo(() => {
    // Backwards compatibility - only allow custom faceblock on server-side faceblocked images.
    if (!shouldUseFaceblockedUrls) {
      return undefined;
    }
    if (
      watermarkSettings?.faceblockMode === FACEBLOCK_MODE.CUSTOM_ICON &&
      watermarkSettings?.faceblockCustomIconAssetImage
    ) {
      return watermarkSettings.faceblockCustomIconAssetImage?.url;
    }

    if (watermarkSettings?.faceblockMode === FACEBLOCK_MODE.BLUR) {
      return null;
    }

    // Default is use Waldo face.
    return undefined;
  }, [shouldUseFaceblockedUrls, faceblockMode, watermarkSettings]);

  const onYearbookPhotoToggle = async () => {
    if (yearbookPhoto) {
      await onYearbookPhotoDelete({
        input: {
          album_folder_id: albumFolder.id,
          identity_id: identity?.id,
        },
        existingMatchedPhotoId: matchedPhoto.id,
      });

      return;
    }
    const thumbnail = getThumbBySize(
      photo.thumbnail_urls,
      IMAGE_SIZE_CODE.LARGE2X,
    );
    const onSetPhoto = async crop => {
      await onYearbookPhotosSet({
        input: {
          album_folder_id: albumFolder.id,
          yearbook_photos: [
            {
              photo_id: matchedPhoto.photo.id,
              identity_id: identity?.id,
              // The centered crop is a percent in numbers from 1 - 100, so we must divide
              // to get the system-valid facebox.
              facebox: {
                x: crop.x / 100,
                y: crop.y / 100,
                width: crop.width / 100,
                height: crop.height / 100,
              },
            },
          ],
        },
        setMatchedPhotoId: matchedPhoto.id,
        existingMatchedPhotoId: existingYearbookMatchedPhotoId,
      });
    };
    const centeredCrop = onFaceCenterCrop({
      photoToUse: matchedPhoto.photo,
      container: {
        naturalWidth: thumbnail.width,
        naturalHeight: thumbnail.height,
      },
      aspectRatio:
        albumFolder.yearbook_settings.thumbnail_settings.width /
        albumFolder.yearbook_settings.thumbnail_settings.height,
      isFullSizeFaceBox:
        albumFolder.yearbook_settings.thumbnail_settings.full_size_facebox,
    });
    if (existingYearbookMatchedPhotoId) {
      requestConfirm({
        message: (
          <div className="text-center">
            <h4 className="text-center">
              Overwrite previous yearbook photo selection?
            </h4>
            There is an existing yearbook photo selected for{' '}
            <span className="text-primary">{identity.name}</span>.<br /> Replace
            with this one instead?
          </div>
        ),
        confirmText: 'Yes, replace with this one',
        cancelText: 'No, go back',
        onConfirm: async () => {
          await onSetPhoto(centeredCrop);
        },
        onCancel: () => {
          // Do nothing
        },
      });
    } else {
      await onSetPhoto(centeredCrop);
    }
  };

  const containerRef = useRef(null);
  const [size, setSize] = useState({ width: 0, height: 0 });

  useEffect(() => {
    if (!containerRef.current) return () => {};

    const resizeObserver = new ResizeObserver(([entry]) => {
      const { width, height } = entry.contentRect;
      setSize({ width, height });
    });
    resizeObserver.observe(containerRef.current);

    return () => resizeObserver.disconnect();
  }, [containerRef]);

  return (
    <div
      ref={containerRef}
      {...swipeHandlers}
      className={classNames({
        'matched-grid-photo': true,
        'has-bottom-elements': includeTagName && tagName,
        [className]: !!className,
      })}
      style={{
        ...printBackgroundStyles,
      }}
    >
      <div className="matched-grid-photo-overflow-hidden">
        <PhotoObfuscated
          id={id}
          thumbs={thumbs}
          faceboxes={faceboxes}
          shouldUseFaceblockedUrls={shouldUseFaceblockedUrls}
          obfuscatorMultiplier={obfuscatorMultiplier}
          blockerMinSizeThreshold={0.005}
          blockerMinSize={0.3}
          blockerMaxSize={0.5}
          minThumbnailSize={IMAGE_SIZE_CODE.MEDIUM2X}
          maxThumbnailSize={maxThumbSize}
          faceblockerImageUrl={faceblockerImageUrl}
          enableFaceblock={enableFaceblock}
          onClick={
            (isEntitled || isAllPhotos) && linkTo
              ? () => history.push(linkTo)
              : undefined
          }
          className="img-block"
        />

        {enableWatermark &&
          !shouldUseWatermarkedUrls &&
          size.width > 0 &&
          size.height > 0 && (
            <div className="watermark-container">
              <WatermarkOverlay
                text={watermarkSettings?.wallpaperCustomText}
                fontSize={watermarkSettings?.wallpaperFontSize}
                wallpaperMode={watermarkSettings?.wallpaperWatermarkMode}
                containerWidth={size.width}
                containerHeight={size.height}
                imageUrl={watermarkSettings?.wallpaperAssetImage?.url}
              />
            </div>
          )}
      </div>

      {actions && <div>{actions}</div>}

      {includeTagName && tagName && (
        <Badge
          className="matched-grid-photo-tag"
          badgeStyle="dark-transparent-light"
        >
          {tagName}
        </Badge>
      )}
      {availablePrintBackgrounds?.length > 1 &&
        foregroundThumbnails?.length > 0 && (
          <MatchedPhotoBackgroundSelectionActions
            printBackgrounds={availablePrintBackgrounds}
            foregroundThumbnais={foregroundThumbnails}
            onPrevBackground={onPrevBackground}
            onNextBackground={onNextBackground}
          />
        )}
      {isYearbookEvent && albumFolder?.yearbook_settings && (
        <Fragment>
          {!!yearbookPhoto && (
            <div className="matched-grid-photo-tag">
              <Badge
                badgeSize="sm"
                badgeStyle="dark-transparent-light"
                className="text-extra-bold"
              >
                YEARBOOK PHOTO
              </Badge>
            </div>
          )}
          {albumFolder?.yearbook_settings?.consumer_selection_enabled &&
            isYearbookEntity(album) &&
            photo?.faces?.total === 1 &&
            (!yearbookPhoto ||
              yearbookPhoto?.creator_account?.id === account?.id ||
              yearbookPhoto?.creator_account?.id === vendorAccount?.id) && (
              <div className="matched-grid-yearbook-photo-toggle">
                <CustomControl
                  type="checkbox"
                  shape="squared"
                  size="sm"
                  readOnly
                  checked={!!yearbookPhoto}
                  onChange={onYearbookPhotoToggle}
                  label="Select as yearbook photo"
                />
              </div>
            )}
        </Fragment>
      )}
    </div>
  );
};

MatchedPhoto.propTypes = propTypes;

export default MatchedPhoto;
